import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Upload as UploadIcon, FileText, Loader2, CheckCircle } from "lucide-react";
import logo from "@/assets/logo_medium.png";
import { onboardingCopy } from "@/lib/onboarding-copy";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingProgress } from "@/components/OnboardingProgress";
import { OnboardingFooter } from "@/components/OnboardingFooter";
import ErrorBanner from "@/components/ErrorBanner";
import { AppError, toAppError } from "@/lib/errors";
import { uploading } from "@/services/uploading";

interface UploadedFile {
  id: string;
  name: string;
  status: 'uploading' | 'processing' | 'completed' | 'error';
}

const Upload = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientId = location.state?.patientId;
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<AppError | null>(null);
  const [monitoredDocuments, setMonitoredDocuments] = useState<Set<string>>(new Set());

  useEffect(() => {
    // DEPRECATED: Este fluxo está sendo migrado para /onboarding/miafile
    toast.info("⚠️ Este fluxo está descontinuado. Redirecionando para nova versão...", { duration: 3000 });
    setTimeout(() => navigate("/onboarding/miafile"), 3000);
  }, [navigate]);

  // Realtime listener para monitorar documentos
  useEffect(() => {
    if (monitoredDocuments.size === 0) return;

    const channels = Array.from(monitoredDocuments).map(docId => {
      return supabase
        .channel(`document-${docId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'documents_onboarding',
            filter: `id=eq.${docId}`
          },
          (payload) => {
            console.log('Document updated:', payload);

            if (payload.new.processing_status === 'completed') {
              toast.success('✅ Análise concluída! Documento processado.');

              setFiles(prev => prev.map(f =>
                f.id === docId ? { ...f, status: 'completed' } : f
              ));

              // Remover do monitoramento
              setMonitoredDocuments(prev => {
                const next = new Set(prev);
                next.delete(docId);
                return next;
              });
            } else if (payload.new.processing_status === 'failed') {
              const errorMsg = payload.new.ai_summary || 'Erro na análise. Tente novamente.';

              // Verificar se é erro de documento muito grande
              const isTooBig = errorMsg.includes('muito grande') ||
                errorMsg.includes('muito extenso') ||
                errorMsg.includes('Divida em');

              if (isTooBig) {
                toast.error('📄 Documento muito extenso. Divida em arquivos menores (máx. 10 páginas cada).', {
                  duration: 6000
                });
              } else {
                toast.error(`❌ ${errorMsg}`, { duration: 5000 });
              }

              setFiles(prev => prev.map(f =>
                f.id === docId ? { ...f, status: 'error' } : f
              ));

              setMonitoredDocuments(prev => {
                const next = new Set(prev);
                next.delete(docId);
                return next;
              });
            }
          }
        )
        .subscribe();
    });

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [monitoredDocuments]);

  const handleFileUpload = useCallback(async (fileList: FileList) => {
    setUploadError(null);

    const acceptedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];

      if (!acceptedTypes.includes(file.type)) {
        toast.error(`Formato não suportado: ${file.name}`);
        continue;
      }

      const tempId = crypto.randomUUID();
      setFiles(prev => [...prev, {
        id: tempId,
        name: file.name,
        status: 'uploading'
      }]);

      try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          throw new AppError("RLS_DENY", "Usuário não autenticado");
        }

        // Buscar ou criar appointment para o paciente
        const { data: existingAppointment } = await supabase
          .from('appointments')
          .select('id')
          .eq('patient_id', patientId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        let appointmentId: string;
        if (existingAppointment) {
          appointmentId = existingAppointment.id;
        } else {
          const { data: newAppointment, error: aptError } = await supabase
            .from('appointments')
            .insert({
              patient_id: patientId,
              appointment_date: new Date().toISOString(),
              notes: 'Upload durante onboarding'
            })
            .select('id')
            .single();

          if (aptError) throw new AppError("DB_UPDATE", aptError.message);
          appointmentId = newAppointment.id;
        }

        toast.info(onboardingCopy.upload.uploading);
        setFiles(prev => prev.map(f =>
          f.id === tempId ? { ...f, status: 'processing' } : f
        ));

        // Usar processo UPLOADING unificado
        const result = await uploading(patientId, appointmentId, file, {
          name: 'Paciente',
          cpf: '',
          age: null,
          sex: null
        });

        // Atualizar status para processing
        setFiles(prev => prev.map(f =>
          f.id === tempId
            ? { ...f, id: result.documentId, status: 'processing' }
            : f
        ));

        // Adicionar ao monitoramento realtime
        setMonitoredDocuments(prev => {
          const next = new Set(prev);
          next.add(result.documentId);
          return next;
        });

        toast.success(onboardingCopy.upload.success);
      } catch (error) {
        console.error("Upload error:", error);
        const appErr = toAppError(error);
        setUploadError(appErr);
        setFiles(prev => prev.map(f =>
          f.id === tempId ? { ...f, status: 'error' } : f
        ));
      }
    }
  }, [patientId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFileUpload(e.dataTransfer.files);
    }
  }, [handleFileUpload]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  }, [handleFileUpload]);

  const handleContinue = async () => {
    const hasCompletedFiles = files.some(f => f.status === 'completed');

    if (!hasCompletedFiles) {
      toast.error("Envie pelo menos um exame antes de continuar");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        await supabase
          .from('profiles')
          .update({ onboarding_step: 4 })
          .eq('id', user.id);
      }

      navigate("/onboarding/analysis", { state: { patientId } });
    } catch (error) {
      console.error("Progress update error:", error);
      navigate("/onboarding/analysis", { state: { patientId } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-3xl animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-8" />
        </div>

        <OnboardingProgress currentStep={2} totalSteps={3} />

        <Card className="shadow-medium">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                {onboardingCopy.upload.title}
              </h1>
              <p className="text-muted-foreground">
                {onboardingCopy.upload.description}
              </p>
            </div>

            <label
              className={`
                block w-full border-2 border-dashed rounded-xl p-12 text-center 
                cursor-pointer transition-all
                ${isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/50"}
              `}
              onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDrop={handleDrop}
            >
              <input
                type="file"
                className="hidden"
                multiple
                onChange={handleFileInput}
                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
              />

              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <UploadIcon className="h-12 w-12 text-muted-foreground" />
                  <FileText className="h-5 w-5 text-primary absolute -bottom-1 -right-1" />
                </div>
                <div>
                  <p className="font-medium text-foreground">
                    A Elinia está analisando os documentos e criando o perfil do pacienteviar
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    PDF, JPG, PNG, XLSX, CSV (máx. 20MB) • Processamento instantâneo com IA
                  </p>
                </div>
              </div>
            </label>

            {uploadError && (
              <div className="mt-6">
                <ErrorBanner error={uploadError} />
              </div>
            )}

            {files.length > 0 && (
              <div className="mt-8 space-y-3">
                {files.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                    <FileText className="w-5 h-5 text-secondary" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {file.status === 'uploading' && onboardingCopy.upload.uploading}
                        {file.status === 'processing' && 'Processando documento... A análise com IA continua em segundo plano'}
                        {file.status === 'completed' && 'Processado com sucesso'}
                        {file.status === 'error' && 'Erro no processamento'}
                      </p>
                    </div>
                    {file.status === 'uploading' && <Loader2 className="w-5 h-5 animate-spin text-secondary" />}
                    {file.status === 'processing' && <Loader2 className="w-5 h-5 animate-spin text-secondary" />}
                    {file.status === 'completed' && <CheckCircle className="w-5 h-5 text-secondary" />}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-8 space-y-4">
              <Button
                onClick={handleContinue}
                disabled={!files.some(f => f.status === 'completed')}
                className="w-full"
                size="lg"
              >
                {onboardingCopy.upload.continueButton}
              </Button>

              <p className="text-sm text-center text-muted-foreground">
                {onboardingCopy.upload.tip}
              </p>
            </div>
          </CardContent>
        </Card>

        <OnboardingFooter />
      </div>
    </div>
  );
};

export default Upload;
